import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Home, Save, Plus, Trash2 } from 'lucide-react';

const AdminServices = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchContent();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/services-content', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İçerik yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Çıkış yapıldı');
  };

  const handleChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const handleServiceChange = (index, field, value) => {
    const services = [...content.services];
    services[index] = { ...services[index], [field]: value };
    setContent({ ...content, services });
  };

  const handleServiceFeatureChange = (serviceIndex, features) => {
    const services = [...content.services];
    services[serviceIndex].features = features.split('\n').filter(f => f.trim() !== '');
    setContent({ ...content, services });
  };

  const addService = () => {
    const services = [...content.services, {
      title: 'Yeni Hizmet',
      description: '',
      features: []
    }];
    setContent({ ...content, services });
  };

  const removeService = (index) => {
    const services = content.services.filter((_, i) => i !== index);
    setContent({ ...content, services });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/services-content', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast.success('İçerik kaydedildi');
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-[#1e4a7a]">MİGA Yapı - Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Siteyi Görüntüle
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin/dashboard" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Ana Sayfa İçeriği
            </Link>
            <Link to="/admin/about" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Kurumsal
            </Link>
            <Link to="/admin/services" className="text-[#1e4a7a] font-medium border-b-2 border-[#1e4a7a] pb-4">
              Hizmetlerimiz
            </Link>
            <Link to="/admin/contact" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
            <Link to="/admin/faq" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
              İletişim
            <Link to="/admin/faq" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
            </Link>
            <Link to="/admin/projects" className="text-gray-600 hover:text-[#1e4a7a] pb-4">
              Proje Yönetimi
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Hizmetler Sayfa İçeriği</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Header Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Başlık Bölümü</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Sayfa Başlığı</Label>
                  <Input
                    value={content?.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Alt Başlık</Label>
                  <Textarea
                    value={content?.subtitle || ''}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    rows={2}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="border-b pb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Hizmetler</h3>
                <Button type="button" onClick={addService} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Hizmet Ekle
                </Button>
              </div>
              <div className="space-y-6">
                {content?.services?.map((service, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold">Hizmet {index + 1}</h4>
                      {content.services.length > 1 && (
                        <Button
                          type="button"
                          onClick={() => removeService(index)}
                          variant="outline"
                          size="sm"
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>Başlık</Label>
                        <Input
                          value={service.title}
                          onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Açıklama</Label>
                        <Textarea
                          value={service.description}
                          onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                          rows={2}
                          required
                        />
                      </div>
                      <div>
                        <Label>Özellikler (her satıra bir özellik)</Label>
                        <Textarea
                          value={service.features.join('\n')}
                          onChange={(e) => handleServiceFeatureChange(index, e.target.value)}
                          rows={4}
                          placeholder="Proje Danışmanlığı&#10;Yasal İşlemler&#10;İnşaat Yönetimi"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Process Steps */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Çalışma Süreci Adımları</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Adım 1 Başlık</Label>
                  <Input
                    value={content?.process_step1_title || ''}
                    onChange={(e) => handleChange('process_step1_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 1 Açıklama</Label>
                  <Input
                    value={content?.process_step1_desc || ''}
                    onChange={(e) => handleChange('process_step1_desc', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 2 Başlık</Label>
                  <Input
                    value={content?.process_step2_title || ''}
                    onChange={(e) => handleChange('process_step2_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 2 Açıklama</Label>
                  <Input
                    value={content?.process_step2_desc || ''}
                    onChange={(e) => handleChange('process_step2_desc', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 3 Başlık</Label>
                  <Input
                    value={content?.process_step3_title || ''}
                    onChange={(e) => handleChange('process_step3_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 3 Açıklama</Label>
                  <Input
                    value={content?.process_step3_desc || ''}
                    onChange={(e) => handleChange('process_step3_desc', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 4 Başlık</Label>
                  <Input
                    value={content?.process_step4_title || ''}
                    onChange={(e) => handleChange('process_step4_title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Adım 4 Açıklama</Label>
                  <Input
                    value={content?.process_step4_desc || ''}
                    onChange={(e) => handleChange('process_step4_desc', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-6 border-t">
              <Button type="submit" disabled={saving} className="bg-[#dc2626] hover:bg-[#b91c1c]">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminServices;
